/**
 * Composable pour l'édition d'une quote dans le détail
 * Limite: 100 lignes max
 */

import { ref } from 'vue'
import { toast } from 'vue-sonner'
import { quotesService } from '../../services/index.js'
import { usePostHog } from '../usePostHog.js'

export function useQuoteDetailEdit(quoteId, quote, canEdit) {
  const isEditing = ref(false)
  const editedQuote = ref({})
  const saving = ref(false)
  const { captureException } = usePostHog()

  const startEditing = () => {
    if (!canEdit.value) return
    editedQuote.value = JSON.parse(JSON.stringify(quote.value))
    if (!editedQuote.value.dates_disponibilites || !Array.isArray(editedQuote.value.dates_disponibilites)) {
      editedQuote.value.dates_disponibilites = []
    }
    if (!editedQuote.value.dates_periodes || !Array.isArray(editedQuote.value.dates_periodes)) {
      editedQuote.value.dates_periodes = []
    }
    isEditing.value = true
  }

  const cancelEditing = () => {
    editedQuote.value = {}
    isEditing.value = false
  }

  const saveQuote = async () => {
    if (!canEdit.value) return
    
    saving.value = true
    try {
      const data = await quotesService.update(quoteId, editedQuote.value)
      quote.value = data
      isEditing.value = false
      editedQuote.value = {}
      toast.success('Devis mis à jour avec succès')
    } catch (err) {
      // Capture error in PostHog with context
      const modifiedFields = Object.keys(editedQuote.value).filter(key => {
        return JSON.stringify(editedQuote.value[key]) !== JSON.stringify(quote.value?.[key])
      })
      
      captureException(err, {
        error_type: 'quote_edit_error',
        quote_id: quoteId,
        modified_fields: modifiedFields.length > 0 ? modifiedFields : null,
        error_message: err.message || 'Erreur lors de la mise à jour du devis'
      })
      
      toast.error(err.message || 'Erreur lors de la mise à jour du devis')
      console.error('Erreur détaillée:', err)
      throw err
    } finally {
      saving.value = false
    }
  }

  // Accès optionnel : la demande n'est chargée qu'au montage, or des watchers peuvent
  // évaluer getValue dès le setup (quote.value encore null).
  const getValue = (field) => {
    return isEditing.value ? editedQuote.value?.[field] : quote.value?.[field]
  }

  return {
    isEditing,
    editedQuote,
    saving,
    startEditing,
    cancelEditing,
    saveQuote,
    getValue
  }
}
